/**
 * iter90 — Wedfaces-style guest cluster panel.
 *
 * Fetches unique face clusters from /public/gallery/{eventId}/guests and
 * renders avatar tiles. Users can multi-select and hit "Show photos with
 * everyone" to trigger the intersection filter — the parent gallery
 * applies the returned photo IDs as a matchState.
 *
 * Props:
 *   - eventId : string
 *   - onIntersection(photoIds: string[], label: string, guestIds: string[])
 *   - onClose()
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { photoFileUrl } from "@/lib/gallery";
import { Loader2, Users, Sparkles, X, CheckCircle2 } from "lucide-react";

export default function GuestsPanel({ eventId, onIntersection, onClose }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [guests, setGuests] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true); setErr("");
        const { data } = await axios.get(`${API}/public/gallery/${eventId}/guests`);
        if (!cancelled) setGuests(data.guests || []);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || e.message || "Couldn't load guests");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  const toggle = (guestId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(guestId)) next.delete(guestId); else next.add(guestId);
      return next;
    });
  };

  const applyIntersection = async () => {
    if (selected.size === 0) return;
    try {
      setApplying(true);
      const ids = Array.from(selected);
      const { data } = await axios.get(
        `${API}/public/gallery/${eventId}/photos-with-guests?guest_ids=${ids.join(",")}`,
      );
      const labels = guests
        .filter((g) => selected.has(g.guest_id))
        .map((g) => g.label)
        .join(" + ");
      onIntersection(data.photo_ids || [], labels, ids);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Filter failed");
    } finally {
      setApplying(false);
    }
  };

  const selectedCount = selected.size;

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-stone-200" data-testid="guests-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-rose-700" strokeWidth={2} />
          <h3 className="font-heading font-semibold text-stone-900">
            {loading ? "Loading guests…" : `${guests.length} guests spotted`}
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-stone-400 hover:text-stone-900 p-1 -mr-1"
          aria-label="Close guests panel"
          data-testid="guests-panel-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {loading && (
        <div className="p-8 flex items-center justify-center text-stone-400 text-sm">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Clustering faces…
        </div>
      )}

      {err && !loading && (
        <div className="m-4 p-3 bg-rose-50 border border-rose-200 rounded text-xs text-rose-700">
          {err}
        </div>
      )}

      {!loading && !err && guests.length === 0 && (
        <div className="p-8 text-center text-sm text-stone-500">
          No faces have been detected in this gallery yet.
        </div>
      )}

      {!loading && guests.length > 0 && (
        <>
          <div className="p-3 grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-96 overflow-y-auto">
            {guests.map((g) => (
              <GuestTile
                key={g.guest_id}
                guest={g}
                selected={selected.has(g.guest_id)}
                onToggle={() => toggle(g.guest_id)}
              />
            ))}
          </div>

          <div className="border-t border-stone-100 px-4 py-3 flex items-center justify-between gap-3">
            <div className="text-[11px] text-stone-500">
              {selectedCount === 0
                ? "Tap avatars to combine guests"
                : `${selectedCount} selected · photos containing ALL`}
            </div>
            <button
              type="button"
              disabled={selectedCount === 0 || applying}
              onClick={applyIntersection}
              className="inline-flex items-center gap-1.5 bg-rose-700 disabled:bg-stone-300 text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm transition"
              data-testid="guests-apply-intersection"
            >
              {applying ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              {selectedCount >= 2 ? "Show photos with all" : selectedCount === 1 ? "Show photos" : "Combine"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function GuestTile({ guest, selected, onToggle }) {
  const bbox = guest.avatar_bbox;
  const imgW = guest.avatar_img_w || 1;
  const imgH = guest.avatar_img_h || 1;
  // Crop-to-face via background-image + positioning so we don't need <canvas>.
  const bgStyle = useMemo(() => {
    if (!bbox || !imgW || !imgH || !guest.avatar_photo_id) return null;
    const [x1, y1, x2, y2] = bbox;
    const boxW = Math.max(1, x2 - x1);
    const boxH = Math.max(1, y2 - y1);
    // Zoom so the face fills the tile (with 20% padding on each side).
    const padX = boxW * 0.4;
    const padY = boxH * 0.4;
    const cropX = Math.max(0, x1 - padX);
    const cropY = Math.max(0, y1 - padY);
    const cropW = Math.min(imgW - cropX, boxW + 2 * padX);
    const cropH = Math.min(imgH - cropY, boxH + 2 * padY);
    // Convert to background-position/size for a tightly-cropped preview.
    const bgSize = `${(imgW / cropW) * 100}% ${(imgH / cropH) * 100}%`;
    const bgPos = `-${(cropX / cropW) * 100}% -${(cropY / cropH) * 100}%`;
    return {
      backgroundImage: `url(${photoFileUrl(guest.avatar_photo_id)})`,
      backgroundSize: bgSize,
      backgroundPosition: bgPos,
      backgroundRepeat: "no-repeat",
    };
  }, [bbox, imgW, imgH, guest.avatar_photo_id]);

  return (
    <button
      type="button"
      onClick={onToggle}
      className={`relative aspect-square rounded-xl overflow-hidden group focus:outline-none focus:ring-2 focus:ring-rose-500 ${
        selected ? "ring-2 ring-rose-600 shadow-md" : "ring-1 ring-stone-200 hover:ring-stone-300"
      }`}
      data-testid={`guest-tile-${guest.guest_id}`}
    >
      {bgStyle ? (
        <div className="w-full h-full" style={bgStyle} aria-label={guest.label} />
      ) : (
        <div className="w-full h-full bg-stone-100 flex items-center justify-center text-stone-400 text-xs">
          {guest.label}
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-1.5 py-1 text-white text-[10px] font-semibold flex items-center justify-between">
        <span>{guest.label}</span>
        <span className="tabular-nums opacity-90">{guest.photo_count}</span>
      </div>
      {selected && (
        <div className="absolute top-1 right-1 bg-rose-600 text-white rounded-full p-0.5 shadow" data-testid={`guest-tile-selected-${guest.guest_id}`}>
          <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} />
        </div>
      )}
    </button>
  );
}
